import React from 'react';
import {Button, Row, Col} from 'react-bootstrap';
import Component from '../../components/AscComponent';
import {EditAccountInfo, ViewAccountInfo, SubUserPermissionModal,
    ExternalNumberDescriptionModal, ReauthPasswordModal} from "../Elements/AscElements/AccountElements";

// 現在のパスワードの再入力に関わるサーバ側のエラーコード。
// この 3 つで落ちたときは入力を残して原因をそのまま伝える必要がある
// （server/app/controllers/Account.js が返す）。
const REAUTH_ERROR_CODES = [
    "Account_ReauthFailed",
    "Account_ReauthRequired",
    "Account_MailChangeForbidden"
];

export default class Account extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            hm15_id: 0,
            user_id: '',
            family_name: '',
            first_name: '',
            mail_address: '',
            // Issue #443: 変更の有無を判定するための取得時のメールアドレス。
            // 変更する場合はサーバ側でパスワード再入力を必須にしているため、画面でも同じ判定を行う。
            initial_mail_address: '',
            tel_number: '',
            password: '',
            password_new: '',
            password_new_confirm: '',
            blocking: false,
            subUsers: [],
            // 【#541】サブID の作成上限。null は未設定（既定 5 件として扱う）
            subUserLimit: null,
            // Issue #289: 外線番号名称編集用。名称は {外線番号: 名称} のマップで持つ
            externalNumbers: [],
            externalNumberDescriptions: {},
            showExternalNumberModal: false,
            initialExternalNumberDescriptions: {},
            // Issue #118: サブユーザー権限設定モーダル用
            showPermissionModal: false,
            permissionModalSubUser: null,
            // Issue #443: 二要素認証（メールOTP）設定用
            mfaEnabled: false,
            mfaSubUsers: [],
            // 変更の有無を判定するための取得時の値。変更が無ければ API を呼ばない
            initialMfaEnabled: false,
            initialMfaSubUsers: [],
            // /Account/update を呼ぶかの判定に使う取得時のアカウント情報
            initialAccountInfo: {family_name: "", first_name: "", mail_address: "", tel_number: "", subUsers: []},
            // 再認証（本人確認）モーダル用。「設定」を押したときに出す。
            // 入力値は state.password とは別に持つ。パスワード変更ブロックの
            // 「現在のパスワード」と同じ変数を使うと、どちらの入力なのか分からなくなる。
            showReauthModal: false,
            reauthPassword: '',
            reauthError: null,
            isReauthSaving: false,
            // パスワード変更ブロックの開閉。validationHandle と「設定」時の分岐が読む
            password_change: false,
        };
    }
    
    validationHandle = () => {
        let validation_flag;
        let {
            user_id,
            password,
            password_new,
            password_new_confirm,
            tel_number,
            mail_address,
            initial_mail_address,
            password_change,
            subUsers
        } = this.state;

        // Issue #443: メールアドレスは OTP の送信先のため、変更するときは現在のパスワードを必須にする
        // （サーバ側 /Account/update も同じ条件で再認証する）
        const mailChanged = mail_address !== initial_mail_address;

        // Issue #443: 二要素認証も同じくサーバ側で再認証するため、変更したときは
        // 現在のパスワードを必須にする（保存を「確認」に一本化したことによる）
        const mfaChanged = this.hasMfaChange();

        validation_flag = !!(user_id && this.validateMail(mail_address)
            && !this.hasInvalidMfaSubUserMail()
            // 再認証が要るときの現在のパスワードは、
            //   パスワード変更を開いている  → そのブロックの欄（ここで必須にする）
            //   開いていない                → 「設定」押下時の再認証モーダル（ここでは必須にしない）
            // の 2 経路。後者を必須にすると、画面に欄が無いのに「確認」が押せなくなる。
            && ((mailChanged || mfaChanged) && password_change
                ? password && this.validatePassword(password) : true)
            && (password ? this.validatePassword(password) : true)
            && (password_new ? password && this.validatePassword(password_new)
                && password_new_confirm === password_new : true)
            && (tel_number ? this.validateTelNumber(tel_number) : true));

        for (let i = 0; i < subUsers.length; i++) {
            validation_flag = validation_flag && ((!subUsers[i].new && !subUsers[i].password)
                || (subUsers[i].password && this.validatePassword(subUsers[i].password)));
        }

        return validation_flag;
    };

    onClickHandle = pageType => async () => {
        switch (pageType) {
            case 'edit':
                this.setState({pageType: 'view'});
                break;
            case 'back':
                this.setState({pageType: 'edit'});
                break;
            case 'view':
                // 再認証（メールアドレス・二要素認証の変更）で要る現在のパスワードは、
                // 保存を実行する直前にモーダルで聞く。表へ欄を置くと編集画面にしか
                // 無いことになり、確認画面で再認証が失敗したときに直す先が見えない。
                // 「パスワード変更」を開いているときはそのブロックの
                // 「現在のパスワード」を使うため、モーダルは出さない。
                if (this.needsReauth() && !this.state.password_change) {
                    this.setState({showReauthModal: true, reauthPassword: '', reauthError: null});
                    break;
                }
                await this.runSave(this.state.password);
                break;
            default:
                break;
        }
    }

    /** 再認証モーダルの説明文。何を変更したのかで文言を変える */
    getReauthDescription = () => {
        const mailChanged = this.state.mail_address !== this.state.initial_mail_address;
        const mfaChanged = this.hasMfaChange();
        if (mailChanged && mfaChanged) return this.props.langText.Body.AccountReauthNote;
        if (mailChanged) return this.props.langText.Body.AccountMailChangeReauthNote;
        return this.props.langText.Body.AccountMfaChangeReauthNote;
    }

    /**
     * サーバのエラーコードを画面へ出す文言へ引く。
     * showErrorObjectMessage は alert するため、モーダル内へ出す用に文字列で返す版を持つ。
     */
    getApiErrorText = (err, defaultMessage = 'DataSettingError') => {
        const code = this.getApiErrorCode(err) || (err && err.message) || defaultMessage;
        return this.props.langText.Message[code] || this.props.langText.Message[defaultMessage];
    }

    /**
     * 3 つの保存 API を順に呼ぶ。
     *
     * 現在のパスワードは呼び出し側から受け取る。「パスワード変更」を開いているときは
     * state.password、それ以外で再認証が要るときは再認証モーダルの入力値。
     */
    runSave = async (password) => {
        let {
            user_id,
            hm15_id,
            family_name,
            first_name,
            mail_address,
            tel_number,
            password_new,
            subUsers
        } = this.state;
        // モーダルから来たかどうかで失敗時の出し方を変える
        const fromModal = this.state.showReauthModal;
        this.blockUI();
        if (fromModal) this.setState({isReauthSaving: true, reauthError: null});
        try {
                // 外線番号の名称と二要素認証を先に保存し、アカウント情報を最後に更新する。
                // /update はパスワード変更も担うため、先に走らせると後続 API の
                // パスワード再認証が旧パスワードで失敗する。
                //
                // 3つの API はまとめて1つのトランザクションにはできない。
                // 途中で失敗したときにどこまで保存できたのかが分からないと
                // 「もう一度やり直せばよいのか」の判断ができないため、
                // 呼び出しごとに結果を記録し、失敗した対象を名前で伝える。
                // 失敗は { label, err } で保持する。err を捨てるとサーバの
                // エラーコード（Account_ReauthFailed = 「パスワードが違います。」など）を
                // 利用者へ出せなくなり、何を直せばよいか分からなくなる。
                const failed = [];
                let attempted = 0;
                // 【#541】保存中に作成上限へ達し、一部のサブユーザーが作られなかった場合の通知用。
                // サーバーはこの場合も 200 を返す（既に作成した分は Cognito と DB を揃えて確定済み）。
                let subUserLimitReached = null;
                if (this.hasExternalNumberChange()) {
                    attempted++;
                    try {
                        await this.ascAxios("post", `${this.reactContainerPath}/updateExternalNumberDescription`, {
                            updates: this.state.externalNumbers.map(row => ({
                                external_number: row.external_number,
                                description: this.state.externalNumberDescriptions[row.external_number] || ""
                            }))
                        });
                    } catch (err) {
                        console.error('外線番号の名称保存に失敗しました', this.summarizeApiError(err));
                        failed.push({label: this.props.langText.Body.AccountExternalNumberDescriptionTitle, err});
                    }
                }
                if (this.hasMfaChange()) {
                    attempted++;
                    let mfaPayload = {
                        password,
                        mfa_enabled: this.state.mfaEnabled
                    };
                    if (!this.isSubUserSession()) {
                        mfaPayload.sub_users = this.state.mfaSubUsers.map(row => ({
                            hm16_id: row.hm16_id,
                            mail_address: row.mail_address,
                            mfa_enabled: row.mfa_enabled
                        }));
                    }
                    try {
                        await this.ascAxios("post", `${this.reactContainerPath}/updateMfaSetting`, mfaPayload);
                    } catch (err) {
                        console.error('二要素認証の設定保存に失敗しました', this.summarizeApiError(err));
                        failed.push({label: this.props.langText.Body.MfaStatus, err});
                    }
                }
                if (this.hasAccountInfoChange()) {
                    attempted++;
                    try {
                        const updateResult = await this.ascAxios("post", `${this.reactContainerPath}/update`, {
                            user_id,
                            hm15_id,
                            family_name,
                            first_name,
                            mail_address,
                            tel_number,
                            password,
                            password_new,
                            subUsers
                        });
                        if (updateResult && updateResult.data && updateResult.data.sub_user_limit_reached) {
                            subUserLimitReached = updateResult.data.sub_user_limit;
                        }
                    } catch (err) {
                        console.error('アカウント情報の更新に失敗しました', this.summarizeApiError(err));
                        failed.push({label: this.props.langText.Body.AccountInfoTitle, err});
                    }
                }

                if (failed.length > 0) {
                    // 再認証で落ちたときは、どの項目が落ちたかより
                    // 「パスワードが違います。」を出すことが次の操作に直結する。
                    // 入力し直せるようにリロードしない。
                    const reauthFailed = failed
                        .find(row => REAUTH_ERROR_CODES.includes(this.getApiErrorCode(row.err)));
                    if (reauthFailed) {
                        // 外線番号名称の更新はパスワードを要さないため、再認証で落ちる前に
                        // 保存が成立していることがある。何も保存されていないと誤解させないよう、
                        // 一部だけ保存できたときはその旨も併せて出す。
                        const reauthMessage = failed.length === attempted
                            ? this.getApiErrorText(reauthFailed.err)
                            : this.getApiErrorText(reauthFailed.err) + " "
                                + this.props.langText.Message.DataSettingPartialError
                                    .replace("{0}", failed.map(row => row.label).join("、"));
                        // モーダルから来た場合は閉じずにその場で入れ直させる
                        if (fromModal) {
                            this.setState({reauthError: reauthMessage});
                            return;
                        }
                        alert(reauthMessage);
                        return;
                    }
                    if (failed.length === attempted) {
                        // 1 件も保存できていない。DataSettingPartialError は
                        // 「ほかの項目は設定されています。」と断定するため使わず、
                        // サーバのエラーコードをそのまま出す。入力も残す。
                        if (fromModal) {
                            this.setState({reauthError: this.getApiErrorText(failed[0].err)});
                            return;
                        }
                        this.showErrorObjectMessage(failed[0].err, 'DataSettingError');
                        return;
                    }
                    // 一部は保存済みなので、画面を読み込み直して現在の状態を出す。
                    // どれが保存できなかったかは文言で伝える。
                    alert(this.props.langText.Message.DataSettingPartialError
                        .replace('{0}', failed.map(row => row.label).join('、')));
                    window.location.reload();
                    return;
                }
                // 【#541】ほかの変更は保存できているため成功扱いだが、
                // 作成されなかったサブユーザーがあることは必ず伝える。
                if (subUserLimitReached !== null) {
                    alert(this.props.langText.Message.Account_SubUserLimitReachedPartial
                        .replace("{0}", subUserLimitReached));
                } else {
                    alert(this.props.langText.Message.DataSettingSuccess);
                }
                this.props.historyPush("/");
                window.location.reload();
        } catch (err) {
            if (fromModal) {
                this.setState({reauthError: this.getApiErrorText(err)});
                return;
            }
            this.showErrorObjectMessage(err, 'DataSettingError');
            window.location.reload();
        } finally {
            // 解除は finally に一本化する。return で抜けても finally は走るため、
            // 分岐側でも呼ぶと二重解除になる。
            this.unblockUI();
            if (fromModal) this.setState({isReauthSaving: false});
        }
    }

    async componentDidMount(){
        try {
            let result = await this.ascAxios("post", `${this.reactContainerPath}/getAccountInfo`);
            this.setState({
                hm15_id: result.data.id,
                user_id: result.data.user_id,
                family_name: result.data.family_name,
                first_name: result.data.first_name,
                mail_address: result.data.mail_address,
                initial_mail_address: result.data.mail_address,
                // 【#586】二要素認証の確認コードの送信先（サブユーザー自身の送信先。未設定なら空）
                ownMfaMailAddress: this.getOwnMfaMailAddress(result.data),
                tel_number: result.data.tel_number,
                subUsers: result.data.hm16_sub_users,
                oldSubUsers: [...result.data.hm16_sub_users],
                // 【#541】サブID の作成上限。取得時の options から読む
                // （セッションの userInfo だと、管理者が上限を変えても再ログインまで反映されない）。
                subUserLimit: (result.data.options && result.data.options.sub_user_limit) || null,
                ht60_total: result.data.ht60_total,
                voice_expire_display: result.data.voice_expire_display,
                // Issue #289: getAccountInfo のレスポンスに external_numbers が含まれる想定
                // （worker-1 が Account/getAccountInfo に description を含める対応を行う）
                externalNumbers: result.data.external_numbers || [],
                externalNumberDescriptions: this.getExternalNumberDescriptions(result.data),
                initialExternalNumberDescriptions: this.getExternalNumberDescriptions(result.data),
                pageType: 'edit',
                // Issue #443: 二要素認証の現在値を反映
                mfaEnabled: this.getOwnMfaEnabled(result.data),
                mfaSubUsers: this.getMfaSubUsers(result.data),
                // 保存対象を絞るための取得時の値。変更が無い項目は API を呼ばない
                initialMfaEnabled: this.getOwnMfaEnabled(result.data),
                initialMfaSubUsers: this.getMfaSubUsers(result.data),
                initialAccountInfo: {
                    family_name: result.data.family_name,
                    first_name: result.data.first_name,
                    mail_address: result.data.mail_address,
                    tel_number: result.data.tel_number,
                    // メモは編集で書き換わるため、値だけを写して残す
                    subUsers: (result.data.hm16_sub_users || []).map(row => ({
                        sub_user_id: row.sub_user_id,
                        memo: row.memo || ""
                    }))
                }
            });
        } catch (e) {
            console.error(e);
        }
    }

    // Issue #443: サブユーザー自身のセッションか
    isSubUserSession = () => {
        return !!(this.props.userInfo && this.props.userInfo.sub_user_id);
    }

    /**
     * 外線番号 → 名称 のマップを作る（Issue #289）
     * 画面では番号をキーに引くため、配列ではなくマップで持つ。
     */
    getExternalNumberDescriptions = accountInfo => {
        const map = {};
        (accountInfo.external_numbers || []).forEach(row => {
            map[row.external_number] = row.description || "";
        });
        return map;
    }

    onChangeExternalNumberDescription = (externalNumber, value) => {
        // setState はバッチされることがあるため、直前の state は更新関数から受け取る
        this.setState(prev => ({
            externalNumberDescriptions: {
                ...prev.externalNumberDescriptions,
                [externalNumber]: value
            }
        }));
    }

    /** 外線番号の名称に変更があるか */
    hasExternalNumberChange = () => {
        const {externalNumbers, externalNumberDescriptions, initialExternalNumberDescriptions} = this.state;
        return (externalNumbers || []).some(row => {
            const key = row.external_number;
            return (externalNumberDescriptions[key] || "") !== (initialExternalNumberDescriptions[key] || "");
        });
    }

    /** 二要素認証の設定に変更があるか */
    hasMfaChange = () => {
        const {mfaEnabled, initialMfaEnabled, mfaSubUsers, initialMfaSubUsers} = this.state;
        if (mfaEnabled !== initialMfaEnabled) return true;
        if (mfaSubUsers.length !== initialMfaSubUsers.length) return true;
        // 並び順に依存しないよう hm16_id で突き合わせる
        return mfaSubUsers.some(row => {
            const before = initialMfaSubUsers.find(item => item.hm16_id === row.hm16_id);
            if (!before) return true;
            return (row.mail_address || "") !== (before.mail_address || "")
                || !!row.mfa_enabled !== !!before.mfa_enabled;
        });
    }

    /**
     * アカウント情報（氏名・メール・電話番号・パスワード・サブユーザー）に
     * 変更があるか。変更が無ければ /Account/update を呼ばない。
     */
    hasAccountInfoChange = () => {
        const s = this.state;
        if (s.family_name !== s.initialAccountInfo.family_name) return true;
        if (s.first_name !== s.initialAccountInfo.first_name) return true;
        if (s.mail_address !== s.initialAccountInfo.mail_address) return true;
        if ((s.tel_number || "") !== (s.initialAccountInfo.tel_number || "")) return true;
        if (s.password_new) return true;
        const before = s.initialAccountInfo.subUsers;
        if (s.subUsers.length !== before.length) return true;
        // 削除で並びが詰まるため、添字ではなく sub_user_id で突き合わせる
        return s.subUsers.some(row => {
            if (row.new || row.password) return true;
            const beforeRow = before.find(item => item.sub_user_id === row.sub_user_id);
            if (!beforeRow) return true;
            return (row.memo || "") !== (beforeRow.memo || "");
        });
    }

    /**
     * 現在のパスワード入力が必要か。
     * メールアドレスと二要素認証はいずれもサーバ側で再認証するため、
     * どちらかを変更したときはパスワードが要る。
     */
    needsReauth = () => {
        return this.state.mail_address !== this.state.initial_mail_address || this.hasMfaChange();
    }

    /** サーバが返したエラーコードを取り出す。取れないときは空文字 */
    getApiErrorCode = (err) => {
        return (err && err.response && err.response.data && err.response.data.code) || "";
    }

    /**
     * console へ出すためにエラーの要点だけを抜き出す。
     *
     * ascAxios は axios のエラーをそのまま throw し、axios のエラーは
     * error.config.data にリクエスト本文（JSON 文字列）を保持している。
     * そのため err をそのまま console.error へ渡すと、この画面では
     * 現在のパスワード・新しいパスワード・サブユーザーの新規パスワードが
     * 平文でコンソールへ出る。status と code だけに絞る。
     */
    summarizeApiError = (err) => {
        return {
            status: (err && err.response && err.response.status) || null,
            code: this.getApiErrorCode(err) || null,
            message: (err && err.message) || null
        };
    }

    /** サブユーザーの送信先メールに形式不正があるか */
    hasInvalidMfaSubUserMail = () => {
        return this.state.mfaSubUsers.some(row =>
            !!row.mail_address && !this.validateMail(row.mail_address));
    }

    /**
     * 自分の二要素認証の現在値を取得する（Issue #443）
     * サブユーザーのセッションでは自分の hm16 レコードの値を参照する。
     */
    getOwnMfaEnabled = accountInfo => {
        if (this.isSubUserSession()) {
            let ownSubUser = (accountInfo.hm16_sub_users || [])
                .find(row => row.sub_user_id === this.props.userInfo.sub_user_id);
            return !!(ownSubUser && ownSubUser.options && ownSubUser.options.mfa
                && ownSubUser.options.mfa.email_enabled);
        }
        return !!(accountInfo.options && accountInfo.options.mfa && accountInfo.options.mfa.email_enabled);
    }

    /**
     * 【#586】サブユーザーのセッションで、確認コードを送るサブユーザー自身のアドレスを返す。
     * サーバーの resolveMfaTarget と同じく hm16_sub_users.options.mail_address を優先し、
     * 未設定（または親ユーザーのセッション）のときは空文字を返して親ユーザーのアドレスを使わせる。
     */
    getOwnMfaMailAddress = accountInfo => {
        if (!this.isSubUserSession()) return "";
        const ownSubUser = (accountInfo.hm16_sub_users || [])
            .find(row => row.sub_user_id === this.props.userInfo.sub_user_id);
        return (ownSubUser && ownSubUser.options && ownSubUser.options.mail_address) || "";
    }

    /**
     * サブユーザーの二要素認証設定一覧を作る（Issue #443）
     * サブユーザーのセッションでは他のサブユーザーを操作できないため空配列を返す。
     */
    getMfaSubUsers = accountInfo => {
        if (this.isSubUserSession()) return [];
        return (accountInfo.hm16_sub_users || []).map(row => ({
            hm16_id: row.id,
            sub_user_id: row.sub_user_id,
            mail_address: (row.options && row.options.mail_address) || '',
            mfa_enabled: !!(row.options && row.options.mfa && row.options.mfa.email_enabled)
        }));
    }

    onChangeMfaSubUser = (index, key, value) => {
        let mfaSubUsers = this.state.mfaSubUsers.map((row, i) =>
            i === index ? {...row, [key]: value} : row
        );
        this.setState({mfaSubUsers});
    }

    // Issue #118: サブユーザー権限設定モーダル
    openPermissionModal = (index) => {
        this.setState({
            showPermissionModal: true,
            permissionModalSubUser: this.state.subUsers[index],
        });
    }

    closePermissionModal = () => {
        this.setState({ showPermissionModal: false, permissionModalSubUser: null });
    }

    saveSubUserPermission = async (hm16_id, options) => {
        try {
            this.blockUI();
            await this.ascAxios("post", `${this.reactContainerPath}/updateSubUserPermission`, {
                hm16_id,
                options
            });
            // ローカルのsubUsersにoptionsを反映
            let subUsers = this.state.subUsers.map(u =>
                u.id === hm16_id ? { ...u, options } : u
            );
            this.setState({ subUsers });
            alert(this.props.langText.Message.DataSettingSuccess);
            this.closePermissionModal();
        } catch (err) {
            this.showErrorObjectMessage(err, 'DataSettingError');
        } finally {
            this.unblockUI();
        }
    }

    render() {
        return (
            <div className='Account'>
                <Row>
                    {/* Conditional rendering based on pageType */}
                    {this.state.pageType === "edit" && (
                        <Col xs={12} md={12}>
                            <EditAccountInfo
                                state={this.state}
                                langText={this.props.langText}
                                onTextChange_Limit={this.onTextChange_Limit}
                                onTextChange={this.onTextChange}
                                charaLimit={this.props.charaLimit}
                                boardWidth={this.props.boardWidth}
                                propSetState={this.propSetState}
                                onPermissionEdit={this.openPermissionModal}
                                externalNumbers={this.state.externalNumbers}
                                parentPermissionData={this.props.userInfo.permission_data}
                                parentOptions={this.props.userInfo.options}
                                isSubUserSession={this.isSubUserSession()}
                                onChangeExternalNumberDescription={this.onChangeExternalNumberDescription}
                                onOpenExternalNumberModal={() => this.setState({showExternalNumberModal: true})}
                                onChangeMfaEnabled={value => this.setState({mfaEnabled: value})}
                                onChangeMfaSubUser={this.onChangeMfaSubUser}
                                reauthRequired={this.needsReauth()}
                            />
                            {/* 【#586】ボタンはモーダルのフッターと同じ見た目（primary / outline-secondary）で右寄せにする。
                                解約フォームのリンクは従来どおり左端に置く */}
                            <div className="account-footer">
                                {!(this.props.userInfo.company_options && this.props.userInfo.company_options.hideCancellationForm) && (
                                    <a
                                        href="https://lp.rec-ace.com/cancellation-form/"
                                        target="_blank"
                                        className="cancel-link"
                                        rel="noopener noreferrer"
                                    >
                                        {this.props.langText.Body.AccountCancelForm}
                                    </a>
                                )}
                                <div className="account-footer-buttons">
                                    {/* 【#586】押せない理由を赤枠だけに頼らず 1 行で伝える */}
                                    {!this.validationHandle() &&
                                        <span className="account-footer-error">{this.props.langText.Body.AccountValidationSummary}</span>}
                                    <Button
                                        id='account-check'
                                        className='account-button'
                                        variant='primary'
                                        onClick={this.onClickHandle('edit')}
                                        disabled={!this.validationHandle()}
                                    >
                                        {this.props.langText.Body.Check}
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    )}

                    {this.state.pageType === "view" && (
                        <Col xs={12} md={12}>
                            <ViewAccountInfo
                                state={this.state}
                                langText={this.props.langText}
                                boardWidth={this.props.boardWidth}
                                externalNumbers={this.state.externalNumbers}
                                isSubUserSession={this.isSubUserSession()}
                            />
                            {/* 【#586】キャンセル → 設定 の順で右寄せにする（モーダルのフッターと同じ並び） */}
                            <div className="account-footer">
                                <div className="account-footer-buttons">
                                    <Button
                                        id='account-back'
                                        className='account-button'
                                        variant='outline-secondary'
                                        onClick={this.onClickHandle('back')}
                                        disabled={!this.validationHandle()}
                                    >
                                        {this.props.langText.Body.Cancel}
                                    </Button>
                                    <Button
                                        id='account-update'
                                        className='account-button'
                                        variant='primary'
                                        onClick={this.onClickHandle('view')}
                                        disabled={!this.validationHandle()}
                                    >
                                        {this.props.langText.Body.Set}
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    )}
                </Row>

                {/* Issue #118: サブユーザー権限設定モーダル */}
                <SubUserPermissionModal
                    show={this.state.showPermissionModal}
                    onHide={this.closePermissionModal}
                    subUser={this.state.permissionModalSubUser}
                    onSave={this.saveSubUserPermission}
                    externalNumbers={this.state.externalNumbers}
                    parentPermissionData={this.props.userInfo.permission_data}
                    parentOptions={this.props.userInfo.options}
                    companySubUserPermission={this.props.userInfo.company_options && this.props.userInfo.company_options.sub_user_permission}
                    langText={this.props.langText}
                />

                {/* Issue #289: 外線番号の名称編集。保存は「確認」で行うため、
                    このモーダルは state を書き換えるだけにしている */}
                <ExternalNumberDescriptionModal
                    show={this.state.showExternalNumberModal}
                    onHide={() => this.setState({showExternalNumberModal: false})}
                    externalNumbers={this.state.externalNumbers}
                    descriptions={this.state.externalNumberDescriptions}
                    onChange={this.onChangeExternalNumberDescription}
                    langText={this.props.langText}
                />
                {/* 再認証（本人確認）。「設定」を押したときに出し、
                    パスワードが違っていたら閉じずにその場で入れ直させる */}
                <ReauthPasswordModal
                    show={this.state.showReauthModal}
                    description={this.getReauthDescription()}
                    password={this.state.reauthPassword}
                    errorMessage={this.state.reauthError}
                    isSaving={this.state.isReauthSaving}
                    onChangePassword={value => this.setState({reauthPassword: value, reauthError: null})}
                    onSubmit={() => this.runSave(this.state.reauthPassword)}
                    onHide={() => this.setState({showReauthModal: false, reauthPassword: '', reauthError: null})}
                    langText={this.props.langText}
                />
            </div>
        );

    }
}
